import { useState } from "react";
import { Plus, Loader2, Eye, Trash2, Check, X } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DocStatusBadge, openBlob } from "@/lib/pipeline";
import { useAuth } from "@/context/AuthContext";

function UploadDialog({ open, onOpenChange, candidateId, docTypes, onSaved }) {
  const [form, setForm] = useState({ document_type: "", document_number: "", issue_date: "", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.document_type || !file) { toast.error("Choose a type and a file"); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
    fd.append("file", file);
    setSaving(true);
    try {
      await api.post(`/candidates/${candidateId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setForm({ document_type: "", document_number: "", issue_date: "", expiry_date: "" });
      setFile(null);
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="candidate-doc-dialog">
        <DialogHeader><DialogTitle>Upload candidate document</DialogTitle><DialogDescription>PDF, JPG or PNG up to 10 MB.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Document type</Label>
            <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
              <SelectTrigger data-testid="cdoc-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{docTypes.map((t) => <SelectItem key={t} value={t} data-testid={`cdoc-type-option-${t}`}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="cdoc-num">Document number</Label><Input id="cdoc-num" value={form.document_number} onChange={(e) => setForm({ ...form, document_number: e.target.value })} data-testid="cdoc-number-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="cdoc-issue">Issue date</Label><Input id="cdoc-issue" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="cdoc-issue-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="cdoc-exp">Expiry date</Label><Input id="cdoc-exp" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="cdoc-expiry-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="cdoc-file">File</Label><Input id="cdoc-file" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cdoc-file-input" /></div>
          <DialogFooter className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="cdoc-submit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const VER = { pending: "text-slate-500", verified: "text-emerald-700", rejected: "text-rose-700" };

function DocRow({ d, canVerify, canDelete, onChanged }) {
  const act = async (fn) => { try { await fn(); onChanged(); } catch (err) { toast.error(formatApiError(err)); } };
  const verify = (status) => {
    const remarks = status === "rejected" ? window.prompt("Reason for rejecting this document?") : null;
    if (status === "rejected" && remarks === null) return;
    act(() => api.patch(`/candidate-documents/${d.id}/verify`, { verification_status: status, remarks }));
  };
  return (
    <tr className="border-t border-slate-100" data-testid={`cdoc-row-${d.id}`}>
      <td className="px-4 py-3 font-semibold text-slate-800">{d.document_type}<div className="text-xs font-mono font-normal text-slate-400">{d.document_number || "—"}</div></td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600 hidden sm:table-cell">{d.issue_date || "—"}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600">{d.expiry_date || "—"}</td>
      <td className="px-4 py-3"><DocStatusBadge status={d.status} testid={`cdoc-status-${d.id}`} /></td>
      <td className="px-4 py-3 text-xs capitalize hidden sm:table-cell"><span className={VER[d.verification_status]} data-testid={`cdoc-verification-${d.id}`}>{d.verification_status}</span>{d.verification_remarks && <div className="text-slate-400 normal-case">{d.verification_remarks}</div>}</td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <Button size="icon" variant="ghost" onClick={() => openBlob(`/candidate-documents/${d.id}/file`).catch(() => toast.error("Could not open file"))} data-testid={`cdoc-view-btn-${d.id}`}><Eye className="w-4 h-4" /></Button>
        {canVerify && <Button size="icon" variant="ghost" className="text-emerald-700" onClick={() => verify("verified")} data-testid={`cdoc-verify-btn-${d.id}`}><Check className="w-4 h-4" /></Button>}
        {canVerify && <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => verify("rejected")} data-testid={`cdoc-reject-btn-${d.id}`}><X className="w-4 h-4" /></Button>}
        {canDelete && <Button size="icon" variant="ghost" className="text-slate-400 hover:text-rose-600" onClick={() => window.confirm("Delete this document?") && act(() => api.delete(`/candidate-documents/${d.id}`))} data-testid={`cdoc-delete-btn-${d.id}`}><Trash2 className="w-4 h-4" /></Button>}
      </td>
    </tr>
  );
}

export function CandidateDocs({ candidate, meta, canUpload, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const canVerify = meta?.caps?.can_checklist;
  return (
    <div className="space-y-4" data-testid="candidate-docs">
      {canUpload && <Button onClick={() => setOpen(true)} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="add-candidate-doc-btn"><Plus className="w-4 h-4 mr-1" /> Upload document</Button>}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{[["Document", 1], ["Issued"], ["Expiry", 1], ["Status", 1], ["Verification"], ["", 1]].map(([h, a]) => <th key={h} className={`px-4 py-3 text-left font-medium ${a ? "" : "hidden sm:table-cell"}`}>{h}</th>)}</tr>
            </thead>
            <tbody>{candidate.documents.map((d) => <DocRow key={d.id} d={d} canVerify={canVerify} canDelete={!!(meta?.caps?.full || meta?.caps?.docs || (meta?.caps?.office_admin && d.uploaded_by === user?.id))} onChanged={onChanged} />)}</tbody>
          </table>
        </div>
        {candidate.documents.length === 0 && <div className="py-10 text-center text-sm text-slate-400" data-testid="cdocs-empty">No documents uploaded yet.</div>}
      </div>
      <UploadDialog open={open} onOpenChange={setOpen} candidateId={candidate.id} docTypes={meta?.doc_types || []} onSaved={onChanged} />
    </div>
  );
}
